import CountBubble from "@components/CountBubble"
import CountdownTimer from "@components/CountdownTimer"
import { Cart, Menu, Search } from "@components/icons"
import Logo from "@components/Logo"
import { PromoProps } from "@components/PromoBanner/common"
import DesktopPromoBanner from "@components/PromoBanner/DesktopPromoBanner"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import useScrollHandler from "@hooks/useScrollHandler"
import React from "react"
import styled, { css } from "styled-components"

import DefaultHeaderContent from "./DefaultHeaderContent"

const Background = styled.div<{
  $collapsePromoBanner?: boolean
  $hasCountdownTimer?: boolean
  $shorten?: boolean
  $showPromoBanner?: boolean
}>`
  ${({
    $collapsePromoBanner,
    $hasCountdownTimer,
    $shorten,
    $showPromoBanner,
  }) => css`
    z-index: 999;
    position: fixed;
    top: ${$hasCountdownTimer && $showPromoBanner && !$collapsePromoBanner
      ? "92px"
      : $showPromoBanner && !$collapsePromoBanner
        ? "36px"
        : $hasCountdownTimer
          ? "56px"
          : 0};
    will-change: transform;
    transform-origin: top;
    transform: ${$shorten
      ? "scaleY(calc(64 / 88)) translateZ(0)"
      : "scaleY(1) translateZ(0)"};
    transition: transform ${animation.quick} ${cubicBezier};
    width: 100%;
    height: 88px;
    background-color: ${colors.offWhite};
  `}
`

const Container = styled.div<{
  $collapsePromoBanner?: boolean
  $hasCountdownTimer?: boolean
  $shorten?: boolean
  $showPromoBanner?: boolean
}>`
  ${({
    $collapsePromoBanner,
    $hasCountdownTimer,
    $shorten,
    $showPromoBanner,
  }) => css`
    z-index: 1000;
    position: fixed;
    top: ${$hasCountdownTimer && $showPromoBanner && !$collapsePromoBanner
      ? "92px"
      : $showPromoBanner && !$collapsePromoBanner
        ? "36px"
        : $hasCountdownTimer
          ? "56px"
          : 0};
    flex-direction: row;
    align-items: center;
    display: flex;
    transform-origin: top;
    transition: height ${animation.quick} ${cubicBezier};
    will-change: height;
    width: 100%;
    height: ${$shorten ? "64px" : "88px"};
    padding-left: 40px;
    padding-right: 20px;
    border-bottom: ${$shorten ? `solid 1px ${colors.dividerLineGray}` : "none"};
  `}
`

const CountdownTimerWrapper = styled.div`
  z-index: 1002;
  position: fixed;
  top: 0;
  transition: height ${animation.quick} ${cubicBezier};
  width: 100%;
  height: 56px;
`

const PromoBannerWrapper = styled.div<{
  $collapse: boolean
  $hasCountdownTimer: boolean
}>`
  ${({ $collapse, $hasCountdownTimer }) => css`
    z-index: 1001;
    position: fixed;
    top: ${$hasCountdownTimer ? "56px" : 0};
    overflow: ${$collapse ? "hidden" : "visibile"};
    transition: height ${animation.quick} ${cubicBezier};
    width: 100%;
    height: ${$collapse ? 0 : 36}px;
  `}
`

const Content = styled.div`
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: flex-end;
  display: flex;
  overflow: hidden;
  height: 100%;
  margin-left: 32px;
`

const defaultHeaderProps = `
  display: flex;
  height: 100%;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`

const Header = styled.div`
  ${defaultHeaderProps}
`

const Headers = styled.div`
  height: 100%;
  min-width: 0;
  flex-grow: 1;
`

const hideAnimatedHeader = `
  opacity: 0;
  transition: opacity ${animation.quick} ${cubicBezier};
  pointer-events: none;
`
const AnimatedHeader = styled.div<{ $hide?: boolean }>`
  ${defaultHeaderProps}
  opacity: 1;
  will-change: opacity;
  transition: opacity ${animation.medium} ${cubicBezier};
  ${({ $hide }) => ($hide ? hideAnimatedHeader : "")}
`

const showFixedHeaderProps = `
  opacity: 1;
  transform: translateY(calc(-50% - 32px)) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const showFixedHeaderWithCountdownTimerProps = `
  opacity: 1;
  transform: translateY(calc(-50% - 88px)) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const FixedHeader = styled.div<{
  $hasCountdownTimer?: boolean
  $show?: boolean
}>`
  position: absolute;
  left: 0;
  right: 0;
  will-change: opacity, transform;
  opacity: 0;
  transform: translateY(-50%) translateZ(0);
  transition:
    opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  pointer-events: none;
  z-index: -1;
  ${({ $hasCountdownTimer, $show }) =>
    $show
      ? $hasCountdownTimer
        ? showFixedHeaderWithCountdownTimerProps
        : showFixedHeaderProps
      : ""}
`

const Button = styled.button`
  display: flex;
  height: 100%;
  padding: 0 12px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  will-change: background-color;
  transition: background-color ${animation.medium} ${cubicBezier};
  background: none;
  border: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media ${breakpoints.desktop} {
    padding: 4px 20px 0;
  }

  @media ${breakpoints.largeDesktop} {
    width: 88px;
    padding: 0;

    &:hover {
      background-color: ${colors.dividerLineGray};
    }
  }
`

const SearchButton = styled(Button)``

const CartButton = styled(Button)`
  @media ${breakpoints.largeDesktop} {
    margin-left: 8px;
  }
`

const MenuButton = styled(Button)``

const Icon = styled.div`
  width: 24px;
  height: 24px;
`

export interface DesktopHeaderProps {
  collapsePromoBanner?: boolean
  countdownTimer?: {
    eventEnd?: string
    eventStart: string
    textAfterEvent?: string
    textBeforeEvent: string
    textDuringEvent?: string
  }
  enterpriseLink: string
  expanded?: boolean
  filters?:
    | {}
    | {
        brand?: {
          name: string
          slug: string
        }
        device?: {
          name: string
          slug: string
        }
        deviceType?: {
          menuLabel?: string
          name: string
          slug: string
        }
        series?: {
          name: string
        }
      }
  FixedHeaderComponent?: React.ReactNode
  fixedHeaderTopRef?: any
  isLoggedIn?: boolean
  megaMenuActiveItem?: string
  megaMenuHeaderItems?: Array<{
    label: string
    onClick?: () => void
  }>
  megaMenuHeaderRef?: any
  onClickCart?: (e) => void
  onClickExplore?: (e) => void
  onClickMenu?: (e) => void
  onClickSearch?: (e) => void
  onLogout?: () => void
  product?: object
  productCount?: number
  promos?: Array<PromoProps>
  promoScrollDelay: string
  rewardsPoints?: number
  searchValue?: string
  shortenDesktopHeader?: boolean
  showFilters?: boolean
  showFixedHeader?: boolean
  showPromoBanner?: boolean
  showRewardsPoints?: boolean
  supportLink: string
}

const DesktopHeader = (props: DesktopHeaderProps) => {
  const { FixedHeaderComponent, fixedHeaderTopRef, showFixedHeader } = props
  const hasCountdownTimer = !!props.countdownTimer

  const [refPos, setRefPos] = React.useState<any>(0)
  const handleShowFixedHeader = () => {
    if (fixedHeaderTopRef && fixedHeaderTopRef.current) {
      setRefPos(fixedHeaderTopRef.current.getBoundingClientRect().top)
    }
  }

  if (fixedHeaderTopRef) {
    useScrollHandler({ onScroll: handleShowFixedHeader })
  }

  const shouldShowFixedHeader =
    fixedHeaderTopRef &&
    refPos &&
    refPos < (hasCountdownTimer ? -116 : -60) &&
    !!showFixedHeader
  return (
    <>
      {props.countdownTimer && (
        <CountdownTimerWrapper>
          <CountdownTimer
            eventEnd={props.countdownTimer.eventEnd}
            eventStart={props.countdownTimer.eventStart}
            textAfterEvent={props.countdownTimer.textAfterEvent}
            textBeforeEvent={props.countdownTimer.textBeforeEvent}
            textDuringEvent={props.countdownTimer.textDuringEvent}
          />
        </CountdownTimerWrapper>
      )}
      {props.showPromoBanner && props.promos && (
        <PromoBannerWrapper
          $collapse={!!props.collapsePromoBanner}
          $hasCountdownTimer={hasCountdownTimer}
        >
          <DesktopPromoBanner
            enterpriseLink={props.enterpriseLink}
            isLoggedIn={props.isLoggedIn}
            onLogout={props.onLogout}
            promos={props.promos}
            promoScrollDelay={props.promoScrollDelay}
            rewardsPoints={props.rewardsPoints}
            showRewardsPoints={props.showRewardsPoints}
            supportLink={props.supportLink}
          />
        </PromoBannerWrapper>
      )}
      <Background
        $collapsePromoBanner={props.collapsePromoBanner}
        $hasCountdownTimer={hasCountdownTimer}
        $shorten={props.shortenDesktopHeader}
        $showPromoBanner={props.showPromoBanner}
      />
      <Container
        $collapsePromoBanner={props.collapsePromoBanner}
        $hasCountdownTimer={hasCountdownTimer}
        $shorten={props.shortenDesktopHeader}
        $showPromoBanner={props.showPromoBanner}
        id="desktopHeader"
      >
        <Logo />
        <Content>
          {showFixedHeader ? (
            <Headers>
              <AnimatedHeader $hide={shouldShowFixedHeader}>
                <DefaultHeaderContent {...props} />
              </AnimatedHeader>
              <FixedHeader
                $hasCountdownTimer={hasCountdownTimer}
                $show={shouldShowFixedHeader}
              >
                {FixedHeaderComponent}
              </FixedHeader>
            </Headers>
          ) : (
            <Header>
              <DefaultHeaderContent {...props} />
            </Header>
          )}
          <SearchButton aria-label="Search" onClick={props.onClickSearch}>
            <Icon>
              <Search />
            </Icon>
          </SearchButton>
          <CartButton aria-label="Cart" onClick={props.onClickCart}>
            <CountBubble count={props.productCount || 0} />
            <Icon>
              <Cart />
            </Icon>
          </CartButton>
          <MenuButton aria-label="Menu" onClick={props.onClickMenu}>
            <Icon>
              <Menu />
            </Icon>
          </MenuButton>
        </Content>
      </Container>
    </>
  )
}

export default DesktopHeader
